import React, { useState, useEffect, useRef } from "react";
import mqttService from "../../../mqttService";
import mqttUtil from "../../../mqttUtil";
import * as sessionService from "@brewery/services/sessionService";
import Button from "@design-system/Button";
import analyticsService, {
  ANALYTICS_TYPES,
} from "@common/services/analyticsService";
import "./UpdateTargetTempSlider.scss";

// The keg serving range, mirroring the backend exactly:
// working_sessions/serializers.py:241 bounds serving_temperature to 1-15 C, and
// :415 reports a breach as "outside the keg range (1-15 C)". tools/keg-skip-cli
// carries the same pair. Note this is NOT recipes/constants.py MIN/MAX_SERVING_
// TEMP (1-20) — that validates a saved recipe, not the keg serving path.
//
// Match it exactly rather than narrowing: the floor is 1 by deliberate decision
// (the 2026-07-20 temp-floor project dropped these from 5 to 1 so cold
// conditioning works again), so a tighter slider silently takes that back.
//
// This is also the ONLY bound on the endpoint the slider calls —
// UpdateProcessSerializer.serving_temperature is a bare FloatField with no
// min/max, so nothing downstream re-checks what we send.
export const MIN_SERVING_TEMP = 1;
export const MAX_SERVING_TEMP = 15;

// How long to wait for the device to confirm before releasing the control. The
// keg answers a CMD_UPDATE_RECIPE in well under a second when it is online, so
// this only fires when the round-trip is lost.
export const CONFIRMATION_TIMEOUT_MS = 15000;

export const STATUS = {
  IDLE: "idle",
  SENDING: "sending",
  FAILED: "failed",
};

// Only rendered while the keg is in SERVING_TEMP_CONTROL_STATE, which is the one
// state where the firmware retargets the Peltier live off this command. See
// ChartHeader for the gate.
//
// Moving the slider changes nothing on the device: it is local state until the
// user presses Apply. That is deliberate — this retargets real hardware, every
// send overwrites the session's pending-command tracking and appends to the
// per-device resend registry, so commit has to be one explicit act rather than
// something a drag can emit repeatedly.
const UpdateTargetTempSlider = ({ targetTemp, session }) => {
  const [pendingTemp, setPendingTemp] = useState(null);
  const [status, setStatus] = useState(STATUS.IDLE);

  // Refs, not plain locals: the command sequence and the timer have to survive
  // the re-render that setStatus triggers, or the MQTT confirmation arrives to
  // a stale binding and the timer can never be cleared on unmount.
  const commandSeq = useRef(null);
  const timeoutId = useRef(null);
  const subscription = useRef(null);

  const value = pendingTemp !== null ? pendingTemp : targetTemp;
  const isSending = status === STATUS.SENDING;
  const isDirty = Number(value) !== Number(targetTemp);

  const release = () => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
    if (subscription.current) {
      subscription.current.unsubscribe();
      subscription.current = null;
    }
  };

  useEffect(() => release, []);

  const settle = () => {
    release();
    commandSeq.current = null;
    // Drop back to the device's own setpoint. The next frequent log carries the
    // new one; showing the device's value rather than ours keeps the control
    // honest if the keg ends up somewhere else.
    setPendingTemp(null);
    setStatus(STATUS.IDLE);
  };

  const fail = () => {
    release();
    commandSeq.current = null;
    // Keep the user's value on screen so Apply can simply be pressed again.
    setStatus(STATUS.FAILED);
  };

  const listenForCommandResponse = () => {
    subscription.current = mqttService.notification$.subscribe(
      (notification) => {
        if (
          !commandSeq.current ||
          commandSeq.current !== notification.commandSeq
        ) {
          return;
        }
        if (mqttUtil.hasError(notification)) {
          fail();
        } else if (mqttUtil.isExecuted(notification)) {
          settle();
        }
      },
    );
  };

  const handleSliderChange = (event) => {
    setPendingTemp(event.target.value);
    if (status === STATUS.FAILED) {
      setStatus(STATUS.IDLE);
    }
  };

  const handleApply = () => {
    if (isSending || !isDirty) {
      return;
    }
    setStatus(STATUS.SENDING);
    commandSeq.current = null;
    listenForCommandResponse();

    // Command type 6 is CMD_UPDATE_RECIPE. serving_temperature is the ONLY field
    // the firmware reads off this command (Machine.cpp:629); the old
    // fermentation_temperature variant was a no-op on the device and is now
    // rejected by the backend serializer, so it is gone.
    sessionService
      .updateSession(session.id, {
        command_type: 6,
        serving_temperature: value,
      })
      .then((response) => {
        analyticsService.track(ANALYTICS_TYPES.SET_SERVING_TEMP, {
          SessionID: session.id,
          Value: value,
        });
        commandSeq.current = response.seq;
        timeoutId.current = setTimeout(fail, CONFIRMATION_TIMEOUT_MS);
      })
      .catch(fail);
  };

  const label = () => {
    if (isSending) {
      return "Setting new target...";
    }
    if (status === STATUS.FAILED) {
      return "Could not set target — try again";
    }
    return `Target: ${value}°C`;
  };

  return (
    <div className="slidecontainer">
      <input
        type="range"
        min={MIN_SERVING_TEMP}
        max={MAX_SERVING_TEMP}
        value={value}
        onChange={handleSliderChange}
        disabled={isSending}
        className="slider"
        id="myRange"
        aria-label="Serving temperature"
      />
      <div className="slidecontainer__actions">
        {/* bordered-light, not secondary: ChartHeader is a dark panel, and the
            design system paints a disabled button rgba(0,0,0,0.03). With
            secondary's dark text that renders dark-on-dark and the button
            disappears whenever it is disabled — which is its resting state. */}
        <Button
          type="button"
          variant="bordered-light"
          onClick={handleApply}
          disabled={isSending || !isDirty}
        >
          Apply
        </Button>
      </div>
      <small className="slidecontainer__label">{label()}</small>
    </div>
  );
};

export default UpdateTargetTempSlider;
